import React, { useRef } from "react";
import { FixedSizeList as List } from "react-window";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";

const UploadProgressModal = ({
  showUploadModal,
  setShowUploadModal,
  uploading,
  uploadStatus,
  uploadItems,
  uploadedCount,
  totalFiles,
  uploadSpeed,
  timeLeft,
  uploadedBytes,
  totalBatchBytes,
  totalBatchSizeMB,
  startUploadProcess,
}) => {
  const modalRef = useRef(null);

  useModalAccessibility({
    isOpen: showUploadModal,
    onClose: () => {
      if (!uploading) {
        setShowUploadModal(false);
      }
    },
    modalRef,
  });

  if (!showUploadModal) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-progress-title"
        aria-busy={uploading ? "true" : "false"}
        tabIndex={-1}
        style={{
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontFamily: "Inter, sans-serif",
          color: "#1e293b",
          outline: "none",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "18px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <h2 id="upload-progress-title" style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>
            Upload Photos
          </h2>
          <button
            onClick={() => {
              if (!uploading) setShowUploadModal(false);
            }}
            disabled={uploading}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: "22px",
              color: "#64748b",
              cursor: uploading ? "not-allowed" : "pointer",
              padding: "4px 8px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Content Body */}
        <div
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Summary Banner Card */}
          <div
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    backgroundColor: "#e0f2fe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  🖼️
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                    {uploadedCount} / {totalFiles} Photos
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    {uploadStatus === "uploading"
                      ? `Speed: ${uploadSpeed} | ${timeLeft}`
                      : "Ready to upload"}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#2563eb" }}>
                {(uploadedBytes / (1024 * 1024)).toFixed(2)} MB / {totalBatchSizeMB} MB
              </div>
            </div>

            {/* Overall Batch Progress Bar */}
            <div
              style={{
                width: "100%",
                height: "8px",
                backgroundColor: "#cbd5e1",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${
                    totalBatchBytes
                      ? Math.min(100, Math.round((uploadedBytes * 100) / totalBatchBytes))
                      : 0
                  }%`,
                  height: "100%",
                  backgroundColor: "#2563eb",
                  borderRadius: "4px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>

          {/* Scrollable File List */}
          <div
            style={{
              maxHeight: "320px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              paddingRight: "4px",
            }}
          >
            <List height={280} itemCount={uploadItems.length} itemSize={68} width="100%">
              {({ index, style }) => {
                const item = uploadItems[index];
                if (!item) return null;
                return (
                  <div style={{ ...style, paddingRight: "8px", paddingBottom: "6px" }}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "10px 14px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <span style={{ fontSize: "15px" }}>📄</span>
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#334155",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.name}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>
                            {item.sizeMB} MB
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "700",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              backgroundColor:
                                item.status === "done"
                                  ? "#dcfce7"
                                  : item.status === "skipped"
                                  ? "#fef9c3"
                                  : item.status === "uploading"
                                  ? "#dbeafe"
                                  : "#f1f5f9",
                              color:
                                item.status === "done"
                                  ? "#15803d"
                                  : item.status === "skipped"
                                  ? "#a16207"
                                  : item.status === "uploading"
                                  ? "#1d4ed8"
                                  : "#64748b",
                            }}
                          >
                            {item.status === "done"
                              ? "✅ Done"
                              : item.status === "skipped"
                              ? "⚠️ Skipped"
                              : item.status === "uploading"
                              ? `${item.progress}%`
                              : "Pending"}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          width: "100%",
                          height: "4px",
                          backgroundColor: "#f1f5f9",
                          borderRadius: "2px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${item.progress}%`,
                            height: "100%",
                            backgroundColor:
                              item.status === "done"
                                ? "#22c55e"
                                : item.status === "skipped"
                                ? "#eab308"
                                : "#2563eb",
                            transition: "width 0.2s linear",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              }}
            </List>
          </div>
        </div>

        {/* Modal Footer / Action Button */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            display: "flex",
            justify: "flex-end",
            alignItems: "center",
          }}
        >
          {uploadStatus === "idle" && (
            <button
              onClick={startUploadProcess}
              style={{
                width: "100%",
                padding: "12px 24px",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                border: "none",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
              }}
            >
              Start Upload ({totalBatchSizeMB} MB)
            </button>
          )}

          {uploadStatus === "uploading" && (
            <button
              disabled
              style={{
                width: "100%",
                padding: "12px 24px",
                backgroundColor: "#94a3b8",
                color: "#ffffff",
                border: "none",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "not-allowed",
              }}
            >
              Uploading... {uploadedCount}/{totalFiles}
            </button>
          )}

          {uploadStatus === "completed" && (
            <button
              onClick={() => setShowUploadModal(false)}
              style={{
                width: "100%",
                padding: "12px 24px",
                backgroundColor: "#16a34a",
                color: "#ffffff",
                border: "none",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              Done ✅ (Close)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadProgressModal;
